import { createReducer, createAction } from '@reduxjs/toolkit';
import { getDemoNCFileById } from '@components/tours/helpers/demoDataRegistry';
import moment from 'moment';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { backendCall, createApiCall, eventCall } from '@reducers/utils';
import {
  isWeb,
  formatTimeEvent,
  convertTimeLetter,
  machineStatus,
  machineStatusName,
  subStatuses,
  subStatusName,
  isPhone,
  localTime,
  Alert,
} from '../../helpers';

import { getPartByID } from '@reducers/parts';
import { setDataToMachineStore } from '@reducers/machines';
import { KEYS_TAGS } from '@components/imageHandling/common';

import { trackEvent, TRACKING_EVENTS, getErrorType } from '@services/tracking';
import type { AppThunk, RootState } from '@store/types';
import { isRealEvent } from './utils';
import type {
  ManufactureState,
  EventsState,
  RenderEventSection,
  NCFilesData,
  NcFileSearchResult,
  GroupedMachineEvent,
  ManufactureMachineEventSearchResponse,
  ManufactureNcFileSearchResponse,
  NcFile,
  MachineEventsTimelineDetailsResponse,
  MachineEventsTimelineResponse,
  MachineEventsTimelineDetailsRequestDto,
  MachineEventsTimelineRequestDto,
  AddProductionInformationRequestDto,
  IgnoreProductionInformationRequestDto,
  SetChangeProductionInformationRequestDto,
  SetSetupProductionInformationRequestDto,
  RemoveProductionInformationRequestDto,
  SearchMachineEventsRequest,
  SearchNcFilesRequest,
  ODataParams,
  MachineEvent,
  GroupedMachineEventWithDuration,
  SearchMachineEventsParams,
  SearchMachineEventsCbData,
  GetMachineEventForDayParams,
  MachineEventDetailsParams,
  MachineEventDetailsWallboardParams,
  GetMachineEventTimelineForDayParams,
  NCFileFilterParams,
  GetNCFilesOptions,
  FinishProcessingNCFileImageParams,
} from './types';

// Re-export types for consumers
export type {
  ManufactureState,
  ManufactureMachineEventSearchResponse,
  ManufactureNcFileSearchResponse,
  NcFile,
  MachineEventsTimelineDetailsResponse,
  MachineEventsTimelineResponse,
  GroupedMachineEvent,
  NcFileSearchResult,
} from './types';

// === Initial State ===

const initState: ManufactureState = {
  loaded: false,
  data: {},
  ncFiles: {
    processing: false,
    loaded: false,
    canUpload: true,
    data: {
      ncFiles: [],
      totalResultCount: 0,
    },
    page: 0,
    showLoading: true,
    isError: false,
    pageSize: 100,
    pagination: {
      HasNext: true,
    },
    date: null,
  },
  selectedNCFile: {},
  events: {
    page: 1,
    topPage: 1,
    pageSize: 50,
    data: [],
    dataOptimazed: [
      {
        type: 'empty',
      },
    ],
    amountForKeep: 100,
    renderEvents: [
      {
        title: 'empty',
        data: Array.from({ length: 25 }, () => ({ type: 'empty' })),
      },
    ],
    timeLine: [],
    ncFiles: [],
    loaded: false,
    noData: false,
    hasTimeNext: true,
    hasNext: true,
    hasPrev: false,
    timeInputRange: [],
    timeOutputRange: [],
    eventInputRange: [],
    eventOutputRange: [],
    timeOffset: 0,
    renderTime: [],
    renderDate: {},
  },
};

// === Actions ===

export const clear = createAction('clear');
export const close = createAction('manufacture/close');
export const setEvents = createAction<Partial<EventsState>>('manufacture/setEvents');
export const clearEvents = createAction('manufacture/clearEvents');
export const updatePage = createAction<string | null>('manufacture/updatePage');
export const updateTopPage = createAction<string | null>('manufacture/updateTopPage');
export const resetPage = createAction('manufacture/resetPage');
export const resetEvents = createAction('manufacture/resetEvents');
export const addLoadingEventsToTop = createAction('manufacture/addLoadingEventsToTop');

export const setSelectedNCFile = createAction<Record<string, unknown>>('manufacture/setSelectedNCFile');
export const updateNCFilesPage = createAction('manufacture/updateNCFilesPage');
export const resetNCFilesPage = createAction('manufacture/resetNCFilesPage');
export const setNCFiles = createAction<{ data: Partial<NCFilesData> }>('manufacture/setNCFiles');
export const setNCFilesError = createAction('manufacture/setNCFilesError');
export const updateEventsDate = createAction<string | null>('manufacture/updateEventsDate');

// === Thunks ===

export const onRefreshedSearchData =
  (e: Record<string, unknown>): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      manufacture: { selectedNCFile },
    } = getState();
    if (e?.searchIndexType === 'parts') {
      const id = selectedNCFile?.machineStatus?.partDetails?.partId;

      if (id) {
        const result = await dispatch(getPartByID(id as number));
        const part = (result as Record<string, unknown[]>)?.searchResult?.[0];
        if (part) {
          dispatch(
            setSelectedNCFile({
              machineStatus: {
                ...selectedNCFile?.machineStatus,
                partDetails: part,
              },
            })
          );
        }
      }
    }
  };

const optimization = (arr1: number[], arr2: number[]) => {
  const new1: number[] = [];
  const new2: number[] = [];
  for (const i in arr1) {
    const index = Number(i);
    if (index === 0 || index === arr1.length - 1) {
      new1.push(arr1[index]);
      new2.push(arr2[index]);
      continue;
    }
    const current1 = arr1[index];
    const prev1 = arr1[index - 1];
    const next1 = arr1[index + 1];
    const current2 = arr2[index];
    const prev2 = arr2[index - 1];
    const next2 = arr2[index + 1];
    if ((prev1 === next1 && prev1 === current1) || (prev2 === next2 && prev2 === current2)) {
      continue;
    }

    if (current1 - prev1 === next1 - current1 && current2 - prev2 === next2 - current2) {
      continue;
    }
    new1.push(arr1[index]);
    new2.push(arr2[index]);
  }
  if (new1.length < 2) {
    new1.unshift(0);
    new1.unshift(0);
  }
  if (new2.length < 2) {
    new2.unshift(0);
    new2.unshift(0);
  }

  return { result1: new1, result2: new2 };
};

const format = 'YYYY-MM-DD HH:mm:ss';

export const getEvent =
  (id: number): AppThunk =>
  (dispatch, getState) => {
    const {
      manufacture: {
        events: { data },
      },
    } = getState();
    const event = data.find((e: GroupedMachineEvent & { sequenceNumber?: number }) => e.sequenceNumber === id);
    return event;
  };

let prevTimePosition = 0;
let prevEventPosition = 0;

export const searchMachineEvents =
  (
    {
      id,
      date,
      initialTimePosition = 0,
      initialEventPosition = 0,
      enablePrevLoading = false,
      direction = 'newer',
      reset = false,
    }: SearchMachineEventsParams,
    cb: false | ((data: SearchMachineEventsCbData) => void) = false
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (reset) {
      dispatch(resetEvents);
    }

    const {
      manufacture: {
        events: { pageSize, page, data = [], topPage, hasNext, hasPrev },
        events,
      },
    } = getState();

    const currentDate = date || events.date;
    const currentLocalDate = currentDate ? localTime(currentDate).startOf('day') : localTime();

    const start = currentLocalDate.utc().format();

    const isOlder = direction === 'older';
    const isNewer = direction === 'newer';

    const pageNumber = isNewer ? page : topPage;

    const params: MachineEventsTimelineDetailsRequestDto = {
      pagination: {
        pageNumber,
        pageSize,
        includeCount: true,
      },
      machineId: id,
      searchBackwards: !isOlder,
      from: start,
      useAsyncFlowForRealtimeQuery: true,
    };

    if (isNewer) {
      dispatch(updatePage(currentDate as string | null));
    } else {
      dispatch(updateTopPage(currentDate as string | null));
    }

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEventTimelineDetail, {
          params,
        })
      )
    );
    let newData: GroupedMachineEventWithDuration[] = ((resp as MachineEventsTimelineDetailsResponse).machineEvents || []).map((i, index) => {
      const item = { ...i };
      if (pageNumber === 1 && index === 0) {
        if (isOlder) {
          item.startedOn = start;
        } else {
          item.endedOn = start;
        }
      }

      return {
        ...item,
        durationInMilliseconds: moment(item.endedOn).diff(item.startedOn, 'milliseconds'),
      };
    });

    if (isOlder) {
      newData = newData.reverse();
    }

    const additionalData: GroupedMachineEventWithDuration[] = [];

    const elHeight = scale(50);
    const ncHeight = scale(48);

    let hasPrevLocal = isOlder ? newData.length === pageSize : hasPrev || enablePrevLoading;
    let hasNextLocal = isNewer ? newData.length === pageSize : hasNext;

    let isNewNCFileBlock = false;
    let fullData: GroupedMachineEventWithDuration[] = [];
    if (isOlder) {
      if (newData[newData.length - 1]?.ncFileMain !== data[0]?.ncFileMain) {
        isNewNCFileBlock = true;
      }
      fullData = [...newData, ...data];
    } else {
      fullData = [...data, ...additionalData, ...newData];
    }
    fullData = JSON.parse(JSON.stringify(fullData));
    const noData = fullData.length === 0;

    const pxInMin = 1;

    const additionalNCFiles: Record<string, boolean> = {};
    let additionalDurationHeight = 0;
    additionalData.forEach((item) => {
      if (item.ncFileMain) {
        additionalNCFiles[item.ncFileMain] = true;
      }
      additionalDurationHeight += scale(((item.durationInMilliseconds || 0) / 1000 / 60) * pxInMin, false);
    });

    const additionalOffset = elHeight * additionalData.length + ncHeight * Object.keys(additionalNCFiles).length || 0;

    const emptyBottomLength = 25;
    const emptyTopLength = 1;
    const topLoadersSize = hasPrevLocal ? ncHeight + elHeight * emptyTopLength : 0;

    let timePosition = initialTimePosition - additionalDurationHeight;
    let eventPosition = initialEventPosition + topLoadersSize;

    if (isNewer && prevEventPosition && !reset) {
      timePosition = prevTimePosition;
      eventPosition = prevEventPosition;
    }

    prevTimePosition = timePosition;
    prevEventPosition = eventPosition;

    let timeRange = [timePosition];
    let eventRange = [eventPosition];

    const renderEvents: RenderEventSection[] = [];

    fullData.forEach((item, index) => {
      if (item.durationInMilliseconds === -1) {
        item.durationInMilliseconds = moment.utc().diff(moment.utc(item.startedOn), 'ms');
      }

      const prev = fullData[index - 1];
      const isNCFile = prev && prev.ncFileMain !== item.ncFileMain;

      if (isNCFile) {
        timeRange.push(timePosition);
        eventRange.push(eventPosition);

        eventPosition += ncHeight;

        timeRange.push(timePosition);
        eventRange.push(eventPosition);
      }

      timeRange.push(timePosition);
      eventRange.push(eventPosition);

      const timeHeight = scale(((item.durationInMilliseconds || 0) / 1000 / 60) * pxInMin, false);

      eventPosition += elHeight;
      timePosition += timeHeight;

      timeRange.push(timePosition);
      eventRange.push(eventPosition);

      if (index === 0 && !renderEvents.length) {
        return renderEvents.push({
          title: item.ncFileMain ?? '',
          data: [item],
        });
      }

      const last = renderEvents[renderEvents.length - 1];

      if (item.ncFileMain === last.title) {
        last.data.push(item);
      } else {
        const lastItem = last.data[last.data.length - 1];
        if (isRealEvent(lastItem)) {
          lastItem.noBorder = true;
        }
        renderEvents.push({
          title: item.ncFileMain ?? '',
          data: [item],
        });
      }
    });

    const timeInput = optimization(eventRange, timeRange);
    const eventInput = optimization(timeRange, eventRange);

    if (isWeb) {
      renderEvents.forEach((section) => {
        const firstItem = section.data?.[0];
        section.firstStart = moment.utc(isRealEvent(firstItem) ? firstItem.endedOn : undefined).format('YYYY-MM-DD');

        let allDuration = 0;
        section.data?.filter(isRealEvent).forEach((d) => {
          allDuration += d.durationInMilliseconds;

          const isNotProductive = d.status === machineStatus.NOTPRODUCTIVE;
          const isSubStatusActive = d.subStatus === subStatuses.Active;

          const renderStatus = isNotProductive
            ? isSubStatusActive
              ? `${i18n.t('Active')} (${i18n.t('Not Productive')})`
              : i18n.t(subStatusName[d.subStatus as keyof typeof subStatusName])
            : i18n.t(machineStatusName[d.status as keyof typeof machineStatusName]);

          const time = convertTimeLetter(d.durationInMilliseconds, true);
          const renderTime = formatTimeEvent(time);

          let isTooLong = false;
          const minInPx = isPhone ? 2 : 4;

          const maxLineWidth = isPhone ? 220 : 320;
          const minLineWidth = 1;

          let lineWidth = (d.durationInMilliseconds / 1000 / 60) * minInPx;

          if (lineWidth > maxLineWidth) {
            lineWidth = maxLineWidth;
            isTooLong = true;
          }

          if (lineWidth < minLineWidth) {
            lineWidth = minLineWidth;
          }

          Object.assign(d, { renderStatus, renderTime, isTooLong, lineWidth });
        });

        section.allDuration = formatTimeEvent(convertTimeLetter(allDuration, true));
      });
    }

    if (hasNextLocal) {
      renderEvents.push({
        title: 'empty',
        data: Array.from({ length: emptyBottomLength }, () => ({ type: 'empty' })),
      });
    }

    if (hasPrevLocal) {
      renderEvents.unshift({
        title: 'empty',
        data: Array.from({ length: emptyTopLength }, () => ({ type: 'empty' })),
      });
    }

    const newTimeInput: Record<number, { inputRange: number[]; outputRange: number[] }> = {};
    timeInput.result1.forEach((n, index) => {
      const rounded = Math.round(n);
      const key = n < 0 ? 0 : Number(String(rounded).slice(0, -3)) * 1000;

      if (!newTimeInput[key]) {
        const prev = newTimeInput[key - 1000];

        if (prev) {
          newTimeInput[key] = {
            inputRange: [prev.inputRange[prev.inputRange.length - 1]],
            outputRange: [prev.outputRange[prev.outputRange.length - 1]],
          };
        } else {
          newTimeInput[key] = {
            inputRange: [],
            outputRange: [],
          };
        }
      }

      newTimeInput[key].inputRange.push(n);
      newTimeInput[key].outputRange.push(timeInput.result2[index]);
    });

    const newEventInput: Record<number, { inputRange: number[]; outputRange: number[] }> = {};

    eventInput.result1.forEach((n, index) => {
      const rounded = Math.round(n);
      const key = n < 0 ? 0 : Number(String(rounded).slice(0, -3)) * 1000;

      if (!newEventInput[key]) {
        newEventInput[key] = {
          inputRange: [],
          outputRange: [],
        };
      }

      newEventInput[key].inputRange.push(n);
      newEventInput[key].outputRange.push(eventInput.result2[index]);
    });

    await dispatch(
      setEvents({
        data: fullData,
        renderEvents,
        noData,
        newTimeInput,
        newEventInput,
        timeInputRange: timeInput.result1,
        timeOutputRange: timeInput.result2,
        eventInputRange: eventInput.result1,
        eventOutputRange: eventInput.result2,
        hasNext: hasNextLocal,
        hasPrev: hasPrevLocal,
        loaded: true,
      })
    );

    if (cb) {
      let offset = additionalOffset + topLoadersSize;
      if (isOlder) {
        const newDataNCFiles: Record<string, boolean> = {};
        newData.forEach((item) => {
          if (item.ncFileMain) {
            newDataNCFiles[item.ncFileMain] = true;
          }
        });
        offset =
          newData.length * elHeight +
          ncHeight * Object.keys(newDataNCFiles).length -
          (topLoadersSize === 0 ? ncHeight + elHeight * emptyTopLength : ncHeight) +
          (isNewNCFileBlock ? ncHeight : 0);
      }
      cb({
        additionalOffset: offset,
        firstStart: fullData[0]?.endedOn,
        newLastEnd: newData[newData.length - 1]?.startedOn,
      });
    }
  };

export const getMachineEventForDay =
  ({ id, date }: GetMachineEventForDayParams): AppThunk<Promise<ManufactureMachineEventSearchResponse | undefined>> =>
  async (dispatch) => {
    const params: SearchMachineEventsRequest = {
      oDataFilter: undefined,
      search: undefined,
    };

    const searchParams = {
      pagination: {
        pageNumber: 1,
        pageSize: 500,
        includeCount: true,
      },
      filters: {
        machineId: {
          value: String(id),
          valueType: 'long',
        },
        CreatedOnMinUtc: {
          value: moment(date).set({ h: 0, m: 0, s: 0 }).format(format),
          valueType: 'datetime',
        },
        CreatedOnMaxUtc: {
          value: moment(date).set({ h: 23, m: 59, s: 59 }).format(format),
          valueType: 'datetime',
        },
      },
      orderBy: '[{ "FieldName": "CreatedOn", "SortOrder": "Desc" }]',
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchMachineEvents, {
          params: searchParams as SearchMachineEventsRequest,
        })
      )
    );
  };

export const machineEventDetails =
  ({ machineId, sequenceNumberStartedOn, sequenceNumberEndedOn }: MachineEventDetailsParams): AppThunk<Promise<MachineEvent[] | undefined>> =>
  async (dispatch) => {
    const params: ODataParams = {
      pageNumber: 0,
      pageSize: sequenceNumberEndedOn - sequenceNumberStartedOn + 1,
      filter: [
        {
          operator: 'eq',
          value: machineId,
          name: 'machineId',
        },
        {
          operator: 'ge',
          value: sequenceNumberStartedOn,
          name: 'sequenceNumber',
        },
        {
          operator: 'le',
          value: sequenceNumberEndedOn,
          name: 'sequenceNumber',
        },
      ],
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEvent, {
          params,
        })
      )
    );
  };

export const machineEventDetailsWallboard =
  ({ sequenceNumberStartedOn, machineId }: MachineEventDetailsWallboardParams, signal?: AbortSignal): AppThunk<Promise<MachineEvent[] | undefined>> =>
  async (dispatch) => {
    const params: ODataParams = {
      pageNumber: 0,
      pageSize: 1,
      filter: [
        {
          operator: 'eq',
          value: machineId,
          name: 'machineId',
        },
        {
          operator: 'eq',
          value: sequenceNumberStartedOn,
          name: 'sequenceNumber',
        },
      ],
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEvent, {
          params,
          signal,
        })
      )
    );
  };

export const getMachineEventTimelineForDay =
  ({
    id,
    date,
    initialEventDate,
  }: GetMachineEventTimelineForDayParams): AppThunk<Promise<{ machineEventTimelinePoints: Record<string, unknown>[] }>> =>
  async (dispatch) => {
    const start = localTime(date).startOf('day').utc().format();
    const end = initialEventDate ? localTime(initialEventDate).utc().format() : localTime(date).add(1, 'day').startOf('day').utc().format();

    const params: MachineEventsTimelineRequestDto = {
      machineId: id,
      from: start,
      until: end,
      searchBackwards: true,
      pagination: {
        pageNumber: 1,
        pageSize: 1000,
        includeCount: true,
      },
    };

    const resp = await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.machineEventTimeline, {
          params,
        })
      )
    );
    const data = (resp as MachineEventsTimelineResponse).machineEvents || [];

    const isToday = moment.utc(date).isSame(moment(), 'day');

    const filtered = data.reduce((accumulator: Record<string, unknown>[], item, index) => {
      const modified = { ...item } as Record<string, unknown>;
      if (data.length - 1 === index) {
        modified.startedOn = start;
      }
      if (index === 0 && !isToday) {
        modified.endedOn = end;
      }

      return [
        ...accumulator,
        {
          ...modified,
          durationInMilliseconds: moment.utc(modified.endedOn as string).diff(moment.utc(modified.startedOn as string), 'milliseconds'),
        },
      ];
    }, []);

    return { machineEventTimelinePoints: filtered };
  };

export const addProductionInformation =
  (data: AddProductionInformationRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.addProductionInformation, {
          params: data,
          onSuccess: () => {
            trackEvent(TRACKING_EVENTS.NC_FILE, {
              action: 'added',
              ncFileId: String((data as Record<string, unknown>)?.ncFileId || ''),
              fileSizeBytes: ((data as Record<string, unknown>)?.fileSizeBytes as number) || 0,
              uploadMethod: 'filePicker',
              result: 'success',
            });
            trackEvent(TRACKING_EVENTS.ITEM_LINKING, {
              action: 'linked',
              fromItem: 'ncFile',
              toItem: 'batch',
              result: 'success',
            });
          },
          onError: (error) => {
            trackEvent(TRACKING_EVENTS.NC_FILE, {
              action: 'added',
              ncFileId: String((data as Record<string, unknown>)?.ncFileId || ''),
              fileSizeBytes: ((data as Record<string, unknown>)?.fileSizeBytes as number) || 0,
              uploadMethod: 'filePicker',
              result: 'failed',
            });
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const ignoreProductionInformation =
  (data: IgnoreProductionInformationRequestDto): AppThunk<Promise<{ error: true; errorDetails: unknown } | undefined>> =>
  async (dispatch) => {
    let capturedError: unknown;
    await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.ignoreProductionInformation, {
          params: data,
          onSuccess: () => {},
          onError: (error) => {
            capturedError = error;
            console.log(error, 'error');
          },
        })
      )
    );
    if (capturedError) {
      return { error: true, errorDetails: capturedError };
    }
  };

export const setChangeProductionInformation =
  (data: SetChangeProductionInformationRequestDto): AppThunk<Promise<{ error: true; errorDetails: unknown } | undefined>> =>
  async (dispatch) => {
    let capturedError: unknown;
    await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.setChangeProductionInformation, {
          params: data,
          onSuccess: () => {},
          onError: (error) => {
            capturedError = error;
            console.log(error, 'error');
          },
        })
      )
    );
    if (capturedError) {
      return { error: true, errorDetails: capturedError };
    }
  };

export const setSetupProductionInformation =
  (params: SetSetupProductionInformationRequestDto): AppThunk<Promise<unknown>> =>
  async (dispatch) => {
    try {
      const call = createApiCall((api) => api.manufacture.setSetupProductionInformation, { params });

      return await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnSetSetupProductionInformation',
          eventNameError: 'OnFailedToChangeProductionInformation',
          data: call,
        })
      );
    } catch (e) {
      console.log(e, 'error');
      return { error: true, errorDetails: e };
    }
  };

export const removeProductionInformation =
  (params: RemoveProductionInformationRequestDto): AppThunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    try {
      const call = createApiCall((api) => api.manufacture.removeProductionInformation, {
        params,
      });

      const resp = await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnRemovedProductionInformation',
          eventNameError: 'OnFailedToChangeProductionInformation',
          data: call,
        })
      );

      if ((resp as Record<string, unknown>)?.ncFileId) {
        const {
          machines: { machines },
        } = getState() as RootState;

        (machines as Record<string, unknown>[]).forEach((i: Record<string, unknown>) => {
          if ((i?.machineStatus as Record<string, unknown>)?.ncFileId == (resp as Record<string, unknown>).ncFileId) {
            const random = () => `${Math.round(Math.random() * 100)}-${new Date().getTime()}`;
            const data = {
              productionBatchRandomHash: random(),
              machineId: (i as Record<string, unknown>).machineId,
            };
            setDataToMachineStore(data);
          }
        });
      }

      return resp;
    } catch (e) {
      Alert({ type: 'danger', title: ((e as Record<string, unknown>)?.errorMessage as string) || 'Error' });
      console.log(e, 'error');
    }
  };

export const getNCFileByName =
  (name: string, select?: string[]): AppThunk<Promise<ManufactureNcFileSearchResponse | undefined>> =>
  async (dispatch) => {
    const params: SearchNcFilesRequest = {
      oDataFilter: `NcFileName eq '${name}'`,
    };

    if (select) {
      (params as SearchNcFilesRequest & { select?: string[] }).select = select;
    }

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchNcFiles, {
          params,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

let getNCFileByIdSignal: AbortController | null = null;

export const getNCFileById =
  (id: number, select?: string[], externalSignal?: AbortSignal): AppThunk<Promise<NcFile | undefined>> =>
  async (dispatch) => {
    // Demo data guard for tour
    const demoData = getDemoNCFileById(id);
    if (demoData) return demoData;

    const params: ODataParams = {
      select: select?.join(','),
      filter: [
        {
          operator: 'eq',
          value: id,
          name: 'ncFileId',
        },
      ],
    };

    // Callers that manage their own signal (e.g. a component-scoped fetch) opt out of the shared
    // module-level controller, so they neither abort nor get aborted by other getNCFileById callers.
    // Callers that pass nothing keep the original "latest call wins" behavior unchanged.
    let signal: AbortSignal;
    if (externalSignal) {
      signal = externalSignal;
    } else {
      if (getNCFileByIdSignal) {
        getNCFileByIdSignal.abort();
      }
      getNCFileByIdSignal = new AbortController();
      signal = getNCFileByIdSignal.signal;
    }

    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.ncFile, {
          params,
          signal,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
    return (result as NcFile[] | undefined)?.[0];
  };

export const getNCFilesByProductionOperationId =
  (id: number, filters?: NCFileFilterParams): AppThunk<Promise<ManufactureNcFileSearchResponse | undefined>> =>
  async (dispatch) => {
    const params: SearchNcFilesRequest = {
      oDataFilter: `Links/any(link: link/ProductionOperationId eq ${id})`,
      ...(filters?.top ? { top: filters?.top } : {}),
      ...(filters?.skip ? { skip: filters?.skip } : {}),
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchNcFiles, {
          params,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const getNCFilesByProductionOrderId =
  (id: number, filters?: NCFileFilterParams): AppThunk<Promise<ManufactureNcFileSearchResponse | undefined>> =>
  async (dispatch) => {
    const params: SearchNcFilesRequest = {
      oDataFilter: `Links/any(link: link/ProductionOrderId eq ${id})`,
      ...(filters?.top ? { top: filters?.top } : {}),
      ...(filters?.skip ? { skip: filters?.skip } : {}),
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchNcFiles, {
          params,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const addProductionInformationV2 =
  (data: AddProductionInformationRequestDto): AppThunk<Promise<Record<string, unknown>>> =>
  async (dispatch, getState) => {
    const call = createApiCall((api) => api.productionInformation.addProductionInformation, {
      params: data,
    });

    try {
      const resp = await dispatch(
        eventCall({
          connectionName: 'appHub',
          eventName: 'OnAddedProductionInformation',
          eventNameError: 'OnFailedToChangeProductionInformation',
          data: call,
        })
      );

      if ((resp as Record<string, unknown>)?.productionBatchId) {
        const {
          machines: { machines },
        } = getState() as RootState;

        (machines as Record<string, unknown>[]).forEach((i: Record<string, unknown>) => {
          if ((i?.machineStatus as Record<string, unknown>)?.productionBatchId == (resp as Record<string, unknown>).productionBatchId) {
            const random = () => `${Math.round(Math.random() * 100)}-${new Date().getTime()}`;
            const data = {
              productionBatchRandomHash: random(),
              machineId: (i as Record<string, unknown>).machineId,
            };
            setDataToMachineStore(data);
          }
        });
      }

      trackEvent(TRACKING_EVENTS.ITEM_LINKING, {
        action: 'linked',
        fromItem: 'ncFile',
        toItem: 'job',
        result: 'success',
      });
      return resp as Record<string, unknown>;
    } catch (e) {
      trackEvent(TRACKING_EVENTS.ITEM_LINKING, {
        action: 'linked',
        fromItem: 'ncFile',
        toItem: 'job',
        result: 'failed',
      });
      return { error: true, errorDetails: e };
    }
  };

export const getNCFileByPartId =
  (id: number, filters?: NCFileFilterParams): AppThunk<Promise<ManufactureNcFileSearchResponse | undefined>> =>
  async (dispatch) => {
    const params: SearchNcFilesRequest = {
      oDataFilter: `PartId eq ${Number(id)}`,
      ...(filters?.top ? { top: filters.top } : {}),
      ...(filters?.skip ? { skip: filters.skip } : {}),
    };

    return await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchNcFiles, {
          params,
          onError: (error) => {
            console.log(error, 'error');
          },
        })
      )
    );
  };

export const getNCFiles =
  (
    callback: (() => void) | undefined,
    { filter, orderBy, orderByDirection, resetPages, searchValue, resetRecords }: GetNCFilesOptions
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (resetPages) {
      await dispatch(resetNCFilesPage());
    } else {
      await dispatch(updateNCFilesPage());
    }

    const {
      manufacture: {
        ncFiles: { page, pageSize, data },
      },
    } = getState();

    const params: SearchNcFilesRequest = {
      search: searchValue,
      top: pageSize,
      skip: page * pageSize,
      orderBy: [`${orderBy} ${orderByDirection}`],
      oDataFilter: filter.oDataFilter || filter.ncFileStatus ? `NcFileStatus eq '${filter.ncFileStatus}'` : '',
    };

    await dispatch(
      backendCall(
        createApiCall((api) => api.manufacture.searchNcFiles, {
          params,
          onSuccess: (d: ManufactureNcFileSearchResponse) => {
            let ncFiles: NcFileSearchResult[];
            if (resetRecords) {
              ncFiles = d.searchResult || [];
            } else {
              ncFiles = [...data.ncFiles, ...(d.searchResult || [])];
            }
            dispatch(setNCFiles({ data: { ncFiles, totalResultCount: d.totalResultCount } }));
            if (callback) {
              callback();
            }
          },
          onError: (error) => {
            console.log(error, 'error');
            dispatch(setNCFilesError());
          },
        })
      )
    );
  };

export const finishProcessingNCFileImage =
  ({
    imageHash,
    image: { pageNumber, extensionDerivative, extensionOriginal, isCropped, links: tags },
  }: FinishProcessingNCFileImageParams): AppThunk =>
  (dispatch, getState) => {
    const {
      manufacture: {
        ncFiles: {
          data: { ncFiles },
        },
      },
    } = getState();

    if (!ncFiles.length) return;

    const clampingTag = tags?.find((t) => t.scopeId === KEYS_TAGS.Clamping);
    if (!clampingTag || !clampingTag.isFavorite) {
      return;
    }
    let changeDetected = false;
    const ncFilesList = ncFiles.map((n) => {
      if (clampingTag.scopeReferenceId === (n as Record<string, unknown>).ncFileId) {
        changeDetected = true;
        return {
          ...n,
          isProcessing: false,
          processingID: null,
          imageHash: imageHash,
          imageData: {
            pageNumber,
            extensionDerivative,
            extensionOriginal,
            isCropped,
          },
        };
      }
      return n;
    });

    if (changeDetected) {
      dispatch(setNCFiles({ data: { ncFiles: ncFilesList as NcFileSearchResult[] } }));
    }
  };

// === Reducer ===

const getNewObj = (): ManufactureState => JSON.parse(JSON.stringify(initState));

export default createReducer<ManufactureState>(getNewObj(), (builder) => {
  builder
    .addCase(updatePage, (state, action) => ({
      ...state,
      events: {
        ...state.events,
        page: state.events.page + 1,
        date: action.payload,
      },
    }))
    .addCase(updateTopPage, (state, action) => ({
      ...state,
      events: {
        ...state.events,
        topPage: state.events.topPage + 1,
        date: action.payload,
      },
    }))
    .addCase(updateEventsDate, (state, action) => ({
      ...state,
      events: {
        ...state.events,
        date: action.payload,
      },
    }))
    .addCase(setSelectedNCFile, (state, action) => ({
      ...state,
      selectedNCFile: action.payload,
    }))
    .addCase(addLoadingEventsToTop, (state) => ({
      ...state,
      events: {
        ...state.events,
        renderEvents: [
          {
            title: 'empty',
            data: Array.from({ length: 10 }, () => ({ type: 'empty' })),
          },
          ...state.events.renderEvents,
        ],
      },
    }))
    .addCase(resetEvents, (state) => ({
      ...state,
      events: {
        ...state.events,
        page: 1,
        topPage: 1,
        data: [],
        renderEvents: [
          {
            title: 'empty',
            data: Array.from({ length: 25 }, () => ({ type: 'empty' })),
          },
        ],
        loaded: false,
        noData: false,
        hasNext: true,
        hasPrev: false,
        timeInputRange: [],
        timeOutputRange: [],
        eventInputRange: [],
        eventOutputRange: [],
        newTimeInput: {},
        newEventInput: {},
        date: null,
      },
    }))
    .addCase(resetPage, (state) => ({
      ...state,
      events: {
        ...state.events,
        page: 1,
      },
    }))
    .addCase(clearEvents, () => {
      return getNewObj();
    })
    .addCase(setEvents, (state, action) => ({
      ...state,
      events: {
        ...state.events,
        ...action.payload,
      },
    }))
    .addCase(setNCFiles, (state, action) => ({
      ...state,
      ncFiles: {
        ...state.ncFiles,
        loaded: true,
        processing: false,
        data: {
          ...state.ncFiles.data,
          ...action.payload.data,
          ncFiles: action.payload.data.ncFiles ?? state.ncFiles.data.ncFiles,
        },
      },
    }))
    .addCase(updateNCFilesPage, (state) => ({
      ...state,
      ncFiles: {
        ...state.ncFiles,
        processing: true,
        page: state.ncFiles.page + 1,
        data: {
          ...state.ncFiles.data,
          totalResultCount: 0,
        },
      },
    }))
    .addCase(resetNCFilesPage, (state) => ({
      ...state,
      ncFiles: {
        ...state.ncFiles,
        processing: true,
        page: 0,
        data: {
          ...state.ncFiles.data,
          totalResultCount: 0,
        },
      },
    }))
    .addCase(setNCFilesError, (state) => ({
      ...state,
      ncFiles: {
        ...state.ncFiles,
        isError: true,
      },
    }))
    .addCase(clear, () => {
      return getNewObj();
    })
    .addCase(close, () => {
      return getNewObj();
    });
});
